import { ChangeEvent, FocusEvent, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader } from '@googlemaps/js-api-loader';
import { MapPin } from 'react-feather';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Card } from './Card';
import { Div } from './Generic';
import { Icon } from './Icon';
import { InputWrapper } from './InputGroup';
import DebouncedInput from './DebouncedInput';
import FadeIn from './FadeIn';
import Radio, { RadioPill } from './Radio';
import config from '../../config';

export interface GoogleLocationInputProps {
  value: GoogleLocation;
  onChange: (location: GoogleLocation) => any;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  id?: string | null;
  placeholder?: string | null;
}

interface GoogleLocation {
  id: string | null;
  title: string | null;
  description: string | null;
  address: string | null;
  country: string | null;
  coordinates: number[];
}

const GoogleLocationInput = ({ value, onChange, onBlur, placeholder, id }: GoogleLocationInputProps) => {
  const { t } = useTranslation('common');

  return (
    <ErrorBoundary
      fallback={(
        <Card sc={{ outline: 'gray.200' }}>
          {t('validation.something_wrong_try_again')}
        </Card>
      )}
    >
      <InnerGoogleLocationInput value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder} id={id} />
    </ErrorBoundary>
  );
};

const InnerGoogleLocationInput = ({ value, onChange, onBlur, placeholder, id }: GoogleLocationInputProps) => {
  const { t } = useTranslation('common');

  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState(value?.description);
  const inputId = id || `google-location-input-${Math.round(Math.random() * 1000000)}`;

  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<google.maps.places.AutocompleteSuggestion | null>(null);
  const [error, setError] = useState(false);

  const loaded = useGoogleMaps({
    onLoadFailed: () => {
      setError(true);
    },
  });

  /**
   * The session token is used by Google for billing purposes.
   */
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setSearch(value);
    setSelectedSuggestion(null);
    setOpen(!!value);
    setError(false);

    if (!value) {
      setSuggestions([]);
    }

    onChange({
      id: null,
      title: null,
      description: search,
      address: null,
      country: null,
      coordinates: [],
    });
  };

  useEffect(() => {
    let subscribed = true;

    if (loaded && search && !selectedSuggestion) {
      const fetch = async () => {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        }

        try {
          const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: search,
            sessionToken: sessionTokenRef.current,
          });

          if (subscribed) {
            setSuggestions(suggestions || []);
          }
        } catch {
          if (subscribed) {
            setError(true);
          }
        }
      };

      fetch();
    }

    return () => {
      subscribed = false;
    };
  }, [loaded, selectedSuggestion, search]);

  useEffect(() => {
    let subscribed = true;

    if (loaded && selectedSuggestion) {
      setOpen(false);
      setSuggestions([]);
      setSearch(selectedSuggestion.placePrediction.text.text);

      if (searchRef.current) {
        searchRef.current.value = selectedSuggestion.placePrediction.text.text;
      }

      // Start new Autocomplete session.
      sessionTokenRef.current = null;

      const fetch = async () => {
        try {
          const geocoder = new google.maps.Geocoder();

          const { results: [result] } = await geocoder.geocode({
            placeId: selectedSuggestion.placePrediction.placeId,
          });

          if (subscribed) {
            onChange({
              id: selectedSuggestion.placePrediction.placeId,
              title: selectedSuggestion.placePrediction.mainText.text,
              description: selectedSuggestion.placePrediction.text.text,
              address: result.formatted_address,
              country: result.address_components.find(
                (component) => component.types.includes('country'),
              )?.short_name,
              coordinates: [result.geometry.location.lat(), result.geometry.location.lng()],
            });

            setSelectedSuggestion(null);
          }
        } catch {
          if (subscribed) {
            setError(true);
          }
        }
      };

      fetch();
    }

    return () => {
      subscribed = false;
    };
  }, [loaded, selectedSuggestion, onChange]);

  return (
    <Div>
      <InputWrapper>
        <label htmlFor={inputId}>
          <Icon>
            <MapPin />
          </Icon>
        </label>
        <DebouncedInput
          value={search}
          onChange={handleSearch}
          onBlur={onBlur}
          ref={searchRef}
          sc={{ pl: 5, pr: 20 }}
          id={inputId}
          autoComplete="off"
          placeholder={placeholder}
        />
        <label htmlFor={inputId}>
          <img src={PoweredByGoogleImage} alt="Powered by Google" width={100} />
        </label>
      </InputWrapper>

      {error && (
        <Div sc={{ color: 'error', mt: 2 }}>
          {t('validation.something_wrong_try_again')}
        </Div>
      )}

      {open && suggestions.length > 0 && (
        <FadeIn sc={{ mt: 2 }}>
          <RadioPill sc={{ vertical: true }}>
            {suggestions.map((prediction) => (
              <Radio
                sc={{ box: true }}
                onClick={() => setSelectedSuggestion(prediction)}
                key={prediction.placePrediction.placeId}
              >
                {prediction.placePrediction.text.text}
              </Radio>
            ))}
          </RadioPill>
        </FadeIn>
      )}

      {value.address && (
        <MapsContainer sc={{ mt: 2 }}>
          <Map src={`https://maps.googleapis.com/maps/api/staticmap?center=place_id:${encodeURIComponent(value.address)}&markers=${value.coordinates.join(',')}&zoom=13&format=png&maptype=roadmap&style=feature:administrative.land_parcel%7Cvisibility:off&style=feature:administrative.neighborhood%7Cvisibility:off&style=feature:poi%7Celement:labels.text%7Cvisibility:off&style=feature:road%7Celement:labels%7Cvisibility:off&style=feature:water%7Celement:labels.text%7Cvisibility:off&scale=2&size=640x180&key=${config.googleMapsKey}`} />
        </MapsContainer>
      )}
    </Div>
  );
};

interface UsePlacesProps {
  onLoadFailed?: (error: string) => void;
}

const loader = new Loader({ apiKey: config.googleMapsKey });

const useGoogleMaps = ({ onLoadFailed }: UsePlacesProps) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let subscribed = true;

    const load = async () => {
      if (loaded) {
        return;
      }

      try {
        await Promise.all([
          loader.importLibrary('maps'),
          loader.importLibrary('places'),
        ]);

        if (subscribed) {
          setLoaded(true);
        }
      } catch (error) {
        onLoadFailed?.(error);
      }
    };

    load();

    return () => {
      subscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  return loaded;
};

// eslint-disable-next-line max-len
const PoweredByGoogleImage = 'data:image/png;base64,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';

const Map = styled.img`
  ${({ theme }) => css`
    width: 100%;
    border-radius: ${theme.borderRadiuses.md}px;
  `}
`;

const MapsContainer = styled(Div)`
  ${({ theme }) => css`
    position: relative;
    padding-bottom: 28.125%;
    border-radius: ${theme.borderRadiuses.md}px;
    background: rgba(0, 0, 0, .05);

    ${Map} {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  `}
`;

export default GoogleLocationInput;
